import {
  ApiError,
  Button,
  ButtonColor,
  Control,
  ExpandableDivider,
  Field,
  Icon,
  IconName,
  IconSize,
  IconVariant,
  Input,
  InputWidth,
  useToastManager
} from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { type FC, useCallback, useMemo } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import type { WorkspaceFormProps } from "@/pages/ContentSteering/Settings"
import type { AverageBitrateFormType } from "@/pages/ContentSteering/Settings/form"
import { useSegmentList } from "@/queries/segments"
import { useUpdateWorkspace } from "@/queries/workspaces"
import type { SegmentResponse } from "@/types/api"

const maxBitrateValue = 9999

const devices = [
  { key: "desktop", label: "Desktop" },
  { key: "mobile", label: "Mobile" },
  { key: "smarttv", label: "TV" },
  { key: "tablet", label: "Tablet" }
] as const

const AverageBitrateForm: FC<WorkspaceFormProps> = ({ workspace }) => {
  const toastManager = useToastManager()
  const { data: segmentList } = useSuspenseQuery({ ...useSegmentList() })

  const { mutateAsync: updateWorkspace } = useMutation(useUpdateWorkspace())

  const capacityUsed = useMemo(
    () => segmentList.some((segment: SegmentResponse) => segment.balancingMode === "capacity"),
    [segmentList]
  )

  const workspaceDefaultBitrateValue = useMemo(
    () => workspace?.averageBitrates?.default || null,
    [workspace?.averageBitrates?.default]
  )

  const form = useForm<AverageBitrateFormType>({
    defaultValues: {
      averageBitrates: {
        default: workspaceDefaultBitrateValue,
        desktop: workspace?.averageBitrates?.desktop,
        mobile: workspace?.averageBitrates?.mobile,
        smarttv: workspace?.averageBitrates?.smarttv,
        tablet: workspace?.averageBitrates?.tablet
      },
      capacityBalancingEnabled: workspace?.capacityBalancingEnabled || false
    }
  })

  const onAverageBitrateToggle = useCallback(
    async (active?: boolean) => {
      try {
        form.setValue("capacityBalancingEnabled", active || false, {
          shouldDirty: active && !workspace?.averageBitrates?.default
        })

        if (workspace?.averageBitrates?.default && workspace?.averageBitrates?.default > 0) {
          const payload = {
            capacityBalancingEnabled: active
          }

          await updateWorkspace(payload)
        } else {
          if (!active) form.reset()
        }
      } catch (e) {
        if (e instanceof ApiError) {
          toastManager.error({ apiError: e })
        }
      }
    },
    [form, workspace?.averageBitrates?.default, updateWorkspace, toastManager]
  )

  const onAverageBitrateSubmit = form.handleSubmit(async data => {
    const formDefaultBitrateValue = data?.averageBitrates?.default || null

    await onAverageBitrateSave({
      default: formDefaultBitrateValue,
      desktop: data?.averageBitrates?.desktop || formDefaultBitrateValue,
      mobile: data?.averageBitrates?.mobile || formDefaultBitrateValue,
      smarttv: data?.averageBitrates?.smarttv || formDefaultBitrateValue,
      tablet: data?.averageBitrates?.tablet || formDefaultBitrateValue
    })
  })

  const onAverageBitrateSave = useCallback(
    async (averageBitrates: api.switchns.Workspace["averageBitrates"]) => {
      try {
        if (averageBitrates) {
          const payload = {
            averageBitrates,
            capacityBalancingEnabled: form.getValues("capacityBalancingEnabled")
          }

          const res = await updateWorkspace(payload)
          toastManager.success(
            `Average bitrate ${workspace?.averageBitrates.desktop && workspace?.averageBitrates.desktop > -1 ? "updated" : "confirmed"}`
          )
          form.reset({
            averageBitrates: {
              default: res.averageBitrates.default,
              desktop: res.averageBitrates.desktop,
              mobile: res.averageBitrates.mobile,
              smarttv: res.averageBitrates.smarttv,
              tablet: res.averageBitrates.tablet
            },
            capacityBalancingEnabled: res.capacityBalancingEnabled
          })
        }
      } catch (e) {
        if (e instanceof ApiError) {
          toastManager.error({ apiError: e })
        }
      }
    },
    [form, updateWorkspace, toastManager, workspace?.averageBitrates.desktop]
  )

  const resetToDefault = useCallback(async () => {
    await onAverageBitrateSave({
      default: workspaceDefaultBitrateValue,
      desktop: workspaceDefaultBitrateValue,
      mobile: workspaceDefaultBitrateValue,
      smarttv: workspaceDefaultBitrateValue,
      tablet: workspaceDefaultBitrateValue
    })
  }, [workspaceDefaultBitrateValue, onAverageBitrateSave])

  // check if per device bitrates differ from default
  const resetToDefaultVisible =
    !!workspace?.averageBitrates?.default &&
    devices.some(
      device => form.watch(`averageBitrates.${device.key}`) !== workspace?.averageBitrates?.default
    )

  return (
    <form noValidate onSubmit={onAverageBitrateSubmit}>
      <FormProvider {...form}>
        <div className="mt-lg flex flex-col gap-sm">
          <div className="radius-sm flex w-full items-center border border-grey-300 p-sm">
            <div className="w-full font-medium">
              <div className="flex flex-row items-center font-medium">
                <Field
                  label="Capacity balancing"
                  labelClassName="mb-0!"
                  name="capacityBalancingEnabled"
                  showToggle
                  toggleChecked={form.watch("capacityBalancingEnabled")}
                  toggleDataTestId="settings-capacity-balancing-toggle"
                  toggleDisabled={capacityUsed}
                  toggleTooltip={{
                    body: "Existing CDNs use capacity-based balancing",
                    enabled: capacityUsed,
                    placement: "top"
                  }}
                  onToggleChange={e => onAverageBitrateToggle(e.target.checked)}
                />
              </div>
              {form.watch("capacityBalancingEnabled") && (
                <>
                  <Field
                    className="mt-md flex-row"
                    label="Default average bitrate"
                    name="averageBitrates.default"
                    popoverProps={{
                      body: (
                        <p>
                          Enter the default average bitrate per video stream. This value is used to
                          calculate and estimate the total bandwidth.
                        </p>
                      ),
                      placement: "top",
                      showTransitions: true,
                      trigger: "click"
                    }}
                  >
                    {name => (
                      <Controller
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <div className="flex w-full flex-row items-center gap-sm">
                            <Control
                              {...field}
                              as={Input}
                              autoFocus
                              data-testid="settings-average-bitrate-default"
                              max={maxBitrateValue}
                              min={1}
                              suffix="Mbps"
                              type="number"
                              value={field.value ?? ""}
                              width={InputWidth.SMALL}
                              onChange={e => {
                                if (Number(e.target.value) > maxBitrateValue) return
                                const bitrateValue = e.target.value ? Number(e.target.value) : null

                                field.onChange(e)

                                if (
                                  form.watch("averageBitrates.desktop") ===
                                    form.watch("averageBitrates.mobile") &&
                                  form.watch("averageBitrates.mobile") ===
                                    form.watch("averageBitrates.smarttv") &&
                                  form.watch("averageBitrates.smarttv") ===
                                    form.watch("averageBitrates.tablet")
                                ) {
                                  devices.forEach(device => {
                                    form.setValue(`averageBitrates.${device.key}`, bitrateValue)
                                  })
                                }
                              }}
                            />
                          </div>
                        )}
                        rules={{
                          min: { message: "Out of range", value: 1 },
                          required: "Field is required"
                        }}
                      />
                    )}
                  </Field>

                  <ExpandableDivider
                    className="mt-sm w-full"
                    defaultExpanded={resetToDefaultVisible || false}
                    expandContent={
                      <div className="flex flex-row gap-sm">
                        {devices.map(device => (
                          <div key={`div.${device.key}`} className="flex flex-row gap-xs">
                            <Field
                              className="flex-row"
                              label={device.label}
                              name={`averageBitrates.${device.key}`}
                            >
                              {_name => (
                                <Controller
                                  control={form.control}
                                  name={`averageBitrates.${device.key}`}
                                  render={({ field }) => (
                                    <div className="flex w-full flex-row items-end gap-sm">
                                      <Control
                                        {...field}
                                        as={Input}
                                        data-testid={`settings-average-bitrate-${device.key}`}
                                        max={maxBitrateValue}
                                        min={1}
                                        suffix="Mbps"
                                        type="number"
                                        value={field.value ?? ""}
                                        width={InputWidth.SMALL}
                                        onChange={e => {
                                          if (Number(e.target.value) > maxBitrateValue) return
                                          field.onChange(e)
                                        }}
                                      />
                                    </div>
                                  )}
                                  rules={{
                                    min: { message: "Out of range", value: 1 }
                                  }}
                                />
                              )}
                            </Field>
                          </div>
                        ))}
                        {resetToDefaultVisible && (
                          <div className="flex flex-col">
                            <div className="mb-xs flex items-center gap-x-2xs">&nbsp;</div>
                            <div className="flex grow items-center">
                              <Button
                                className="gap-xs text-grey-800"
                                color={ButtonColor.SECONDARY}
                                data-testid="reset-average-bitrate-default-btn"
                                shape="uncontain"
                                size="small"
                                type="button"
                                onClick={() => resetToDefault()}
                              >
                                <Icon
                                  icon={IconName.RESET}
                                  size={IconSize.MEDIUM}
                                  type={IconVariant.REGULAR}
                                />
                                Reset to default
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    }
                  >
                    Custom per device
                  </ExpandableDivider>
                </>
              )}
              {form.formState.isDirty && (
                <div className="row mt-md flex gap-x-sm">
                  <Button
                    aria-label="Cancel"
                    color={ButtonColor.SECONDARY}
                    data-testid="settings-bandwidth-cancel-button"
                    disabled={form.formState.isSubmitting}
                    type="button"
                    onClick={() => form.reset()}
                  >
                    Cancel
                  </Button>

                  <Button
                    aria-label="Update"
                    color={ButtonColor.PRIMARY}
                    data-testid="settings-bandwidth-update-button"
                    disabled={!!form.formState.errors.averageBitrates?.default}
                    isLoading={form.formState.isSubmitting}
                    type="submit"
                  >
                    {workspace?.averageBitrates?.desktop ? "Update" : "Confirm"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  )
}

export default AverageBitrateForm
